import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './StallionStatus.css';
import { CircularProgress } from '@mui/material';

const API_URL = import.meta.env.VITE_API_URL || '';

// Define pending statuses
const PENDING_STATUSES = ['received', 'ready', 'processing'];

const StallionStatus = () => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [shipmentData, setShipmentData] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [showRawData, setShowRawData] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [viewMode, setViewMode] = useState('card');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [addressSummary, setAddressSummary] = useState({});
  const [showAddressSummary, setShowAddressSummary] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [addressSortConfig, setAddressSortConfig] = useState({ key: 'address', direction: 'asc' });
  const [dateFilterFrom, setDateFilterFrom] = useState('');
  const [dateFilterTo, setDateFilterTo] = useState('');
  const [selectAllClients, setSelectAllClients] = useState(false);
  const [aggregatedData, setAggregatedData] = useState(null);
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [clientBreakdown, setClientBreakdown] = useState({});
  const [showEmailTemplate, setShowEmailTemplate] = useState(false);
  const [selectedClientForEmail, setSelectedClientForEmail] = useState(null);
  const [emailTemplate, setEmailTemplate] = useState('');
  const [shipmentCache, setShipmentCache] = useState({});
  const [showShipmentGrouping, setShowShipmentGrouping] = useState(false);
  const [selectedClientForGrouping, setSelectedClientForGrouping] = useState(null);
  const [groupingAnalysis, setGroupingAnalysis] = useState(null);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === 'Yyz123@') {
      setIsAuthenticated(true);
      fetchClients();
    } else {
      setError('Invalid password');
    }
  };

  const fetchClients = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/stallion-clients?password=${password}`);
      setClients(response.data);
      setIsLoading(false);
    } catch (err) {
      setError('Failed to fetch clients');
      setIsLoading(false);
    }
  };

  // Function to normalize address for consistent grouping
  const normalizeAddress = (address1, city, province, postal) => {
    const components = [
      address1 ? address1.trim() : 'Unknown Address',
      city ? city.trim() : '',
      province ? province.trim() : '',
      postal ? postal.trim() : ''
    ].filter(Boolean);
    
    return components.join(', ');
  };

  const fetchAllClientsData = async () => {
    setIsLoading(true);
    setError(null);
    setSelectAllClients(true);
    setSelectedClient('All Clients');
    setSelectedApiKey('all');
    setSelectedStatuses([]);
    setStatusCounts({});
    setAddressSummary({});
    setSelectedAddress('');
    
    try {
      setIsLoadingMore(true);
      
      const allShipmentsData = [];
      const allCounts = {};
      const allCountries = new Set();
      const allAddressCounts = {};
      const allAddressValues = {};
      const breakdown = {};
      
      // Fetch data for all clients
      for (const client of clients) {
        try {
          let shipments;
          const cacheKey = client.API_KEY;
          const now = Date.now();
          const ONE_HOUR = 60 * 60 * 1000; // 1 hour in milliseconds
          
          // Check if we have cached data that's less than 1 hour old
          if (shipmentCache[cacheKey] && 
              shipmentCache[cacheKey].timestamp && 
              (now - shipmentCache[cacheKey].timestamp) < ONE_HOUR) {
            console.log(`Using cached data for ${client.CLIENT}`);
            shipments = shipmentCache[cacheKey].data;
          } else {
            console.log(`Fetching fresh data for ${client.CLIENT}`);
            const response = await axios.get(`${API_URL}/api/stallion-shipments?apiKey=${client.API_KEY}&password=${password}`);
            shipments = response.data.rawData.data || [];
            
            // Update cache
            const newCache = { ...shipmentCache };
            newCache[cacheKey] = {
              data: shipments,
              timestamp: now
            };
            setShipmentCache(newCache);
          }
          
          // Initialize breakdown for this client
          breakdown[client.CLIENT] = {
            clientName: client.CLIENT,
            statuses: {},
            totalBoxes: 0,
            totalValue: 0,
            pendingBoxes: 0,
            pendingValue: 0,
            shipments: []
          };
          
          // Add client info to each shipment and calculate breakdown
          shipments.forEach(shipment => {
            shipment.clientName = client.CLIENT;
            shipment.apiKey = client.API_KEY;
            
            // Apply date filter if specified
            if (dateFilterFrom || dateFilterTo) {
              const shipmentDate = new Date(shipment.created_at);
              const fromDate = dateFilterFrom ? new Date(dateFilterFrom) : null;
              const toDate = dateFilterTo ? new Date(dateFilterTo + 'T23:59:59') : null;
              
              if (fromDate && shipmentDate < fromDate) return;
              if (toDate && shipmentDate > toDate) return;
            }
            
            const status = (shipment.status || 'Unknown').toLowerCase();
            const shipmentValue = parseFloat(shipment.value) || 0;
            
            // Update breakdown stats
            if (!breakdown[client.CLIENT].statuses[status]) {
              breakdown[client.CLIENT].statuses[status] = {
                count: 0,
                value: 0
              };
            }
            
            breakdown[client.CLIENT].statuses[status].count += 1;
            breakdown[client.CLIENT].statuses[status].value += shipmentValue;
            breakdown[client.CLIENT].totalBoxes += 1;
            breakdown[client.CLIENT].totalValue += shipmentValue;
            
            // Track pending shipments separately
            if (PENDING_STATUSES.includes(status)) {
              breakdown[client.CLIENT].pendingBoxes += 1;
              breakdown[client.CLIENT].pendingValue += shipmentValue;
              breakdown[client.CLIENT].shipments.push(shipment);
            }
          });
          
          allShipmentsData.push(...shipments);
        } catch (err) {
          console.error(`Failed to fetch data for client ${client.CLIENT}:`, err);
        }
      }
      
      // Process aggregated data
      allShipmentsData.forEach(shipment => {
        // Apply date filter if specified
        if (dateFilterFrom || dateFilterTo) {
          const shipmentDate = new Date(shipment.created_at);
          const fromDate = dateFilterFrom ? new Date(dateFilterFrom) : null;
          const toDate = dateFilterTo ? new Date(dateFilterTo + 'T23:59:59') : null;
          
          if (fromDate && shipmentDate < fromDate) return;
          if (toDate && shipmentDate > toDate) return;
        }
        
        const status = (shipment.status || 'Unknown').toLowerCase();
        allCounts[status] = (allCounts[status] || 0) + 1;
        
        // Create a consistent address format for grouping
        const fullAddress = normalizeAddress(
          shipment.address1, 
          shipment.city, 
          shipment.province_code, 
          shipment.postal_code
        );
        
        shipment.full_address = fullAddress;
        
        // Calculate shipment value, defaulting to 0 if undefined
        const shipmentValue = parseFloat(shipment.value) || 0;
        
        // Track address counts by status
        if (!allAddressCounts[fullAddress]) {
          allAddressCounts[fullAddress] = {};
          allAddressValues[fullAddress] = {
            totalPendingValue: 0,
            byStatus: {},
            location: {
              address1: shipment.address1 || 'Unknown',
              city: shipment.city || '',
              province: shipment.province_code || '',
              postal: shipment.postal_code || ''
            }
          };
        }
        
        if (!allAddressCounts[fullAddress][status]) {
          allAddressCounts[fullAddress][status] = 0;
          allAddressValues[fullAddress].byStatus[status] = 0;
        }
        
        allAddressCounts[fullAddress][status] += 1;
        allAddressValues[fullAddress].byStatus[status] += shipmentValue;
        
        // Track pending values
        if (PENDING_STATUSES.includes(status)) {
          allAddressValues[fullAddress].totalPendingValue += shipmentValue;
        }
        
        // Extract countries from shipment items
        if (shipment.items && shipment.items.length > 0) {
          shipment.items.forEach(item => {
            if (item.country_of_origin) {
              allCountries.add(item.country_of_origin);
            }
          });
        }
      });
      
      // Convert countries set to sorted array
      const countriesArray = Array.from(allCountries).sort();
      
      // Set the first status as selected if none is selected
      if (Object.keys(allCounts).length > 0) {
        // Prefer to select pending statuses if they exist
        const pendingStatusesFound = PENDING_STATUSES.filter(status => allCounts[status]);
        if (pendingStatusesFound.length > 0) {
          setSelectedStatuses(pendingStatusesFound);
        } else {
          setSelectedStatuses([Object.keys(allCounts)[0]]);
        }
      }
      
      setAggregatedData({ rawData: { data: allShipmentsData } });
      setShipmentData({ rawData: { data: allShipmentsData } });
      setStatusCounts(allCounts);
      setAvailableCountries(countriesArray);
      setAddressSummary({ counts: allAddressCounts, values: allAddressValues });
      setClientBreakdown(breakdown);
      setLastRefreshed(new Date());
      setIsLoading(false);
      setIsLoadingMore(false);
    } catch (err) {
      setError(`Failed to fetch aggregated data: ${err.message}`);
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchShipmentData = async (apiKey, clientName) => {
    setIsLoading(true);
    setError(null);
    setSelectedShipment(null);
    setSelectedCountry('');
    setSelectAllClients(false);
    setAggregatedData(null);
    
    // If this is a new client, update the selected client
    if (apiKey !== selectedApiKey) {
      setSelectedClient(clientName);
      setSelectedApiKey(apiKey);
      setSelectedStatuses([]);
      setStatusCounts({});
      setAddressSummary({});
      setSelectedAddress('');
    }
    
    try {
      setIsLoadingMore(true);
      
      let shipments;
      const now = Date.now();
      const ONE_HOUR = 60 * 60 * 1000; // 1 hour in milliseconds
      
      // Check if we have cached data that's less than 1 hour old
      if (shipmentCache[apiKey] && 
          shipmentCache[apiKey].timestamp && 
          (now - shipmentCache[apiKey].timestamp) < ONE_HOUR) {
        console.log(`Using cached data for ${clientName}`);
        shipments = shipmentCache[apiKey].data;
      } else {
        console.log(`Fetching fresh data for ${clientName}`);
        const response = await axios.get(`${API_URL}/api/stallion-shipments?apiKey=${apiKey}&password=${password}`);
        shipments = response.data.rawData.data || [];
        
        // Update cache
        const newCache = { ...shipmentCache };
        newCache[apiKey] = {
          data: shipments,
          timestamp: now
        };
        setShipmentCache(newCache);
      }
      const counts = {};
      const countries = new Set();
      const addressCounts = {};
      const addressValues = {};
      
      // Count shipments by status and collect all countries
      shipments.forEach(shipment => {
        // Apply date filter if specified for counts
        let includeInCounts = true;
        if (dateFilterFrom || dateFilterTo) {
          const shipmentDate = new Date(shipment.created_at);
          const fromDate = dateFilterFrom ? new Date(dateFilterFrom) : null;
          const toDate = dateFilterTo ? new Date(dateFilterTo + 'T23:59:59') : null;
          
          if (fromDate && shipmentDate < fromDate) includeInCounts = false;
          if (toDate && shipmentDate > toDate) includeInCounts = false;
        }
        
        const status = (shipment.status || 'Unknown').toLowerCase();
        if (includeInCounts) {
          counts[status] = (counts[status] || 0) + 1;
        }
        
        // Create a consistent address format for grouping
        const fullAddress = normalizeAddress(
          shipment.address1, 
          shipment.city, 
          shipment.province_code, 
          shipment.postal_code
        );
        
        shipment.full_address = fullAddress;
        
        // Calculate shipment value, defaulting to 0 if undefined
        const shipmentValue = parseFloat(shipment.value) || 0;
        
        // Track address counts by status (only if included in date filter)
        if (includeInCounts) {
          if (!addressCounts[fullAddress]) {
            addressCounts[fullAddress] = {};
            addressValues[fullAddress] = {
              totalPendingValue: 0,
              byStatus: {},
              location: {
                address1: shipment.address1 || 'Unknown',
                city: shipment.city || '',
                province: shipment.province_code || '',
                postal: shipment.postal_code || ''
              }
            };
          }
          
          if (!addressCounts[fullAddress][status]) {
            addressCounts[fullAddress][status] = 0;
            addressValues[fullAddress].byStatus[status] = 0;
          }
          
          addressCounts[fullAddress][status] += 1;
          addressValues[fullAddress].byStatus[status] += shipmentValue;
          
          // Track pending values
          if (PENDING_STATUSES.includes(status)) {
            addressValues[fullAddress].totalPendingValue += shipmentValue;
          }
        }
        
        // Extract countries from shipment items
        if (shipment.items && shipment.items.length > 0) {
          shipment.items.forEach(item => {
            if (item.country_of_origin) {
              countries.add(item.country_of_origin);
            }
          });
        }
      });
      
      // Convert countries set to sorted array
      const countriesArray = Array.from(countries).sort();
      
      // Set the first status as selected if none is selected
      if (selectedStatuses.length === 0 && Object.keys(counts).length > 0) {
        // Prefer to select pending statuses if they exist
        const pendingStatusesFound = PENDING_STATUSES.filter(status => counts[status]);
        if (pendingStatusesFound.length > 0) {
          setSelectedStatuses(pendingStatusesFound);
        } else {
          setSelectedStatuses([Object.keys(counts)[0]]);
        }
      }
      
      setShipmentData({ rawData: { data: shipments } });
      setStatusCounts(counts);
      setAvailableCountries(countriesArray);
      setAddressSummary({ counts: addressCounts, values: addressValues });
      setLastRefreshed(new Date());
      setIsLoading(false);
      setIsLoadingMore(false);
    } catch (err) {
      setError(`Failed to fetch shipment data: ${err.message}`);
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleStatusToggle = (status) => {
    if (selectedStatuses.includes(status)) {
      // If it's the only selected status, don't remove it
      if (selectedStatuses.length === 1) return;
      setSelectedStatuses(selectedStatuses.filter(s => s !== status));
    } else {
      setSelectedStatuses([...selectedStatuses, status]);
    }
    setSelectedShipment(null);
  };

  const handleSelectAllStatuses = () => {
    setSelectedStatuses(Object.keys(statusCounts));
    setSelectedShipment(null);
  };

  const handleClearStatuses = () => {
    // Keep at least one status selected
    if (Object.keys(statusCounts).length > 0) {
      setSelectedStatuses([Object.keys(statusCounts)[0]]);
    }
    setSelectedShipment(null);
  };

  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
    setSelectedShipment(null);
  };

  const handleRefresh = () => {
    // Clear cache for current selection to force fresh data
    if (selectAllClients) {
      // Clear all client caches
      setShipmentCache({});
      fetchAllClientsData();
    } else if (selectedApiKey) {
      // Clear specific client cache
      const newCache = { ...shipmentCache };
      delete newCache[selectedApiKey];
      setShipmentCache(newCache);
      fetchShipmentData(selectedApiKey, selectedClient);
    }
  };

  const handleShipmentSelect = (shipment) => {
    setSelectedShipment(selectedShipment && selectedShipment.ship_code === shipment.ship_code ? null : shipment);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setSelectedShipment(null);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  const formatCurrency = (value, currency = 'USD') => {
    if (value === undefined || value === null) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
  };

  const handleAddressChange = (e) => {
    setSelectedAddress(e.target.value);
    setSelectedShipment(null);
  };

  const handleToggleAddressSummary = () => {
    setShowAddressSummary(!showAddressSummary);
  };

  const toggleClientExpansion = (clientName) => {
    const newExpanded = new Set(expandedClients);
    if (newExpanded.has(clientName)) {
      newExpanded.delete(clientName);
    } else {
      newExpanded.add(clientName);
    }
    setExpandedClients(newExpanded);
  };

  // Function to create a unique signature for a box based on its items
  const createBoxSignature = (items) => {
    if (!items || items.length === 0) return 'EMPTY';
    
    // Sort items by SKU and create a consistent signature
    const sortedItems = [...items].sort((a, b) => {
      const skuA = a.sku || a.description || '';
      const skuB = b.sku || b.description || '';
      return skuA.localeCompare(skuB);
    });
    
    // Create signature as: sku1:qty1|sku2:qty2|...
    return sortedItems
      .map(item => `${item.sku || item.description || 'unknown'}:${item.quantity || 1}`)
      .join('|');
  };

  // Function to analyze and group identical boxes for a client
  const analyzeClientBoxes = (clientShipments) => {
    const boxGroups = {};
    
    // Group boxes by their item signature
    clientShipments.forEach(shipment => {
      const signature = createBoxSignature(shipment.items);
      
      if (!boxGroups[signature]) {
        boxGroups[signature] = {
          signature: signature,
          items: shipment.items || [],
          shipments: [],
          count: 0
        };
      }
      
      boxGroups[signature].shipments.push(shipment);
      boxGroups[signature].count += 1;
    });
    
    // Separate into 5+ groups and leftover
    const fivePlusGroups = {};
    const leftoverGroups = {};
    
    Object.entries(boxGroups).forEach(([signature, group]) => {
      if (group.count >= 5) {
        fivePlusGroups[signature] = group;
      } else {
        leftoverGroups[signature] = group;
      }
    });
    
    return {
      fivePlus: fivePlusGroups,
      leftover: leftoverGroups,
      totalFivePlusBoxes: Object.values(fivePlusGroups).reduce((sum, g) => sum + g.count, 0),
      totalLeftoverBoxes: Object.values(leftoverGroups).reduce((sum, g) => sum + g.count, 0)
    };
  };

  const generateEmailTemplate = (client) => {
    // Aggregate items by unique description
    const itemAggregation = {};
    
    client.shipments.forEach(shipment => {
      if (shipment.items && shipment.items.length > 0) {
        shipment.items.forEach(item => {
          const description = item.description || item.sku || 'Unknown Item';
          const quantity = parseInt(item.quantity) || 1;
          
          if (itemAggregation[description]) {
            itemAggregation[description] += quantity;
          } else {
            itemAggregation[description] = quantity;
          }
        });
      }
    });
    
    // Sort items by quantity (descending)
    const sortedItems = Object.entries(itemAggregation)
      .sort((a, b) => b[1] - a[1])
      .map(([desc, qty]) => `${qty} x ${desc}`)
      .join('\n');
    
    const template = `We want to update you about an unexpected change with U.S. Customs that is directly impacting Amazon FBA shipments.

Last week, U.S. Customs suddenly began enforcing new rules on how the $800 daily limit under Section 321 is applied. We were only made aware when our FBA shipments started getting stopped at the border.

Before: Each seller could ship up to $800 USD per day to the same Amazon Fulfillment Center. (Example: If 5 sellers each sent a $700 box to York, PA, all 5 would clear.)

Now: U.S. Customs counts the Fulfillment Center as the consignee, not the individual sellers. (Using the same example: York, PA can only receive $800 total per day, across all sellers combined.)

We are expecting to receive goods that haven't been sent over the border back from them. For you, this includes ${client.pendingBoxes} boxes with the following breakdown:

${sortedItems}

We're actively working to provide easy-to-use solutions for both formal and informal clearance leading up to the August 29th changes. If you have a formal shipment you're ready to check, we can help clear it for you ASAP. However, a full process detailed outline will be coming in the next week hopefully.`;
    
    setEmailTemplate(template);
    setSelectedClientForEmail(client.clientName);
    setShowEmailTemplate(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(emailTemplate).then(() => {
      // Show a brief success message
      const button = document.querySelector('.copy-btn');
      const originalText = button.textContent;
      button.textContent = 'Copied!';
      button.style.backgroundColor = '#10b981';
      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = '#3b82f6';
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  const showGroupingAnalysis = (client) => {
    const analysis = analyzeClientBoxes(client.shipments);
    setGroupingAnalysis(analysis);
    setSelectedClientForGrouping(client.clientName);
    setShowShipmentGrouping(true);
  };

  const getFilteredShipments = () => {
    if (!shipmentData || !shipmentData.rawData || !shipmentData.rawData.data) {
      return [];
    }

    const filtered = shipmentData.rawData.data.filter(shipment => {
      // First filter by status
      const shipmentStatus = (shipment.status || 'Unknown').toLowerCase();
      const statusMatches = selectedStatuses.includes(shipmentStatus);
      
      // Then by country if one is selected
      let countryMatches = true;
      if (selectedCountry && shipment.items && shipment.items.length > 0) {
        countryMatches = shipment.items.some(
          item => item.country_of_origin === selectedCountry
        );
      }
      
      // Then by address if one is selected
      let addressMatches = true;
      if (selectedAddress) {
        addressMatches = shipment.full_address === selectedAddress;
      }
      
      // Filter by date range if specified
      let dateMatches = true;
      if (dateFilterFrom || dateFilterTo) {
        const shipmentDate = new Date(shipment.created_at);
        const fromDate = dateFilterFrom ? new Date(dateFilterFrom) : null;
        const toDate = dateFilterTo ? new Date(dateFilterTo + 'T23:59:59') : null; // Include end of day
        
        if (fromDate && shipmentDate < fromDate) {
          dateMatches = false;
        }
        if (toDate && shipmentDate > toDate) {
          dateMatches = false;
        }
      }
      
      return statusMatches && countryMatches && addressMatches && dateMatches;
    });

    // Apply sorting
    return [...filtered].sort((a, b) => {
      if (!a[sortConfig.key] && !b[sortConfig.key]) return 0;
      if (!a[sortConfig.key]) return 1;
      if (!b[sortConfig.key]) return -1;

      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Special handling for dates
      if (sortConfig.key === 'created_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Special handling for numeric values
      if (sortConfig.key === 'weight' || sortConfig.key === 'value' || 
          sortConfig.key === 'length' || sortConfig.key === 'width' || 
          sortConfig.key === 'height') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const renderShipmentItems = (items) => {
    if (!items || items.length === 0) return <p>No items found</p>;
    
    return (
      <div className="items-wrapper">
        {items.map((item, index) => (
          <div key={index} className="item-card">
            <div className="item-header">
              <span className="item-sku">{item.sku || 'No SKU'}</span>
              <span className={`item-origin ${selectedCountry === item.country_of_origin ? 'highlighted' : ''}`}>
                Origin: {item.country_of_origin || 'Unknown'}
              </span>
            </div>
            <div className="item-body">
              <div className="item-description">{item.description || 'No description'}</div>
              <div className="item-details">
                <div className="item-detail">
                  <span className="detail-label">Quantity:</span>
                  <span className="detail-value">{item.quantity || 'N/A'}</span>
                </div>
                <div className="item-detail">
                  <span className="detail-label">Value:</span>
                  <span className="detail-value">{formatCurrency(item.value, item.currency)}</span>
                </div>
                <div className="item-detail">
                  <span className="detail-label">HS Code:</span>
                  <span className="detail-value">{item.hs_code || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getItemsSummary = (items) => {
    if (!items || items.length === 0) return 'No items';
    
    const summary = items.map(item => 
      `${item.quantity}x ${item.description || item.sku} (${item.country_of_origin || 'Unknown'})`
    );
    
    return summary.join(', ');
  };

  const getItemsWithOrigins = (items) => {
    if (!items || items.length === 0) return [];
    
    return items.map(item => {
      return {
        sku: item.sku || 'N/A',
        description: item.description || 'No description',
        quantity: item.quantity || 0,
        value: formatCurrency(item.value, item.currency),
        origin: item.country_of_origin || 'Unknown',
        hsCode: item.hs_code || 'N/A'
      };
    });
  };

  // Function to calculate estimated clearance date based on pending value and processing rate
  const calculateClearanceDate = (pendingValue) => {
    if (!pendingValue || pendingValue <= 0) return null;
    
    const DAILY_PROCESSING_RATE = 800; // $800 per day
    const daysToProcess = Math.ceil(pendingValue / DAILY_PROCESSING_RATE);
    
    const today = new Date();
    const clearanceDate = new Date(today);
    clearanceDate.setDate(today.getDate() + daysToProcess);
    
    return clearanceDate;
  };

  // Format date in MM/DD/YYYY format
  const formatDateShort = (date) => {
    if (!date) return 'N/A';
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  };

  // Handle sorting of address summary table
  const handleAddressSort = (key) => {
    let direction = 'asc';
    if (addressSortConfig.key === key && addressSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setAddressSortConfig({ key, direction });
  };

  // Get sort indicator for address table headers
  const getAddressSortIndicator = (key) => {
    if (addressSortConfig.key !== key) return null;
    return addressSortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Calculate address summary with sorting and filtering applied
  const getAddressSummaryData = () => {
    const summary = {};
    const addressesUnsorted = Object.keys(addressSummary.counts || {});
    
    // Filter shipments by the selected criteria
    const filtered = getFilteredShipments();
    
    // Count shipments by address, considering filters
    if (selectedCountry || dateFilterFrom || dateFilterTo) {
      // When filters are applied, we need to count based on filtered shipments
      addressesUnsorted.forEach(address => {
        summary[address] = {
          address: address,
          location: addressSummary.values?.[address]?.location || {},
          total: 0,
          totalValue: 0,
          pendingValue: 0,
          byStatus: {}
        };
      });
      
      // Count based on filtered shipments
      filtered.forEach(shipment => {
        const address = shipment.full_address;
        const status = (shipment.status || 'Unknown').toLowerCase();
        const shipmentValue = parseFloat(shipment.value) || 0;
        
        if (!summary[address]) return; // Skip if address not found
        
        // Increment status count
        if (!summary[address].byStatus[status]) {
          summary[address].byStatus[status] = {
            count: 0,
            value: 0
          };
        }
        
        // Update counts and values
        summary[address].byStatus[status].count += 1;
        summary[address].byStatus[status].value += shipmentValue;
        summary[address].total += 1;
        summary[address].totalValue += shipmentValue;
        
        // Update pending value
        if (PENDING_STATUSES.includes(status)) {
          summary[address].pendingValue += shipmentValue;
        }
      });
    } else {
      // When no filters are applied, use the pre-calculated counts
      addressesUnsorted.forEach(address => {
        summary[address] = {
          address: address,
          location: addressSummary.values?.[address]?.location || {},
          total: 0,
          totalValue: 0,
          pendingValue: addressSummary.values?.[address]?.totalPendingValue || 0,
          byStatus: {}
        };
        
        // Sum up counts for each selected status
        selectedStatuses.forEach(status => {
          if (addressSummary.counts?.[address]?.[status]) {
            summary[address].byStatus[status] = {
              count: addressSummary.counts[address][status],
              value: addressSummary.values?.[address]?.byStatus?.[status] || 0
            };
            summary[address].total += addressSummary.counts[address][status];
            summary[address].totalValue += addressSummary.values?.[address]?.byStatus?.[status] || 0;
          }
        });
      });
    }
    
    // Add estimated clearance date based on pending value for all addresses
    Object.keys(summary).forEach(address => {
      summary[address].clearanceDate = calculateClearanceDate(summary[address].pendingValue);
      summary[address].daysToClean = summary[address].pendingValue > 0 
        ? Math.ceil(summary[address].pendingValue / 800)
        : 0;
    });
    
    // Convert to array for sorting, removing empty addresses when filtering
    const summaryArray = Object.values(summary)
      .filter(addr => addr.total > 0); // Keep all addresses when not filtering by country
    
    // Apply sorting
    return summaryArray.sort((a, b) => {
      let aValue = a[addressSortConfig.key];
      let bValue = b[addressSortConfig.key];
      
      // Special handling for dates
      if (addressSortConfig.key === 'clearanceDate') {
        // Handle null values for clearance date
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;
        
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      // Special handling for address string
      if (addressSortConfig.key === 'address') {
        aValue = aValue || '';
        bValue = bValue || '';
      }
      
      // Numeric comparison for numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        if (addressSortConfig.direction === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }
      
      // String comparison
      if (addressSortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="stallion-login-container">
        <h2>Stallion Status</h2>
        <form onSubmit={handlePasswordSubmit} className="password-form">
          <div className="form-group">
            <label htmlFor="password">Enter Password:</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="password-input"
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" className="login-button">Login</button>
        </form>
      </div>
    );
  }

  const filteredShipments = getFilteredShipments();
  const totalCount = Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
  const addressSummaryData = getAddressSummaryData();
  const addresses = addressSummaryData.map(addr => addr.address);

  // Function to export filtered data to CSV
  const exportToCSV = () => {
    if (!filteredShipments || filteredShipments.length === 0) {
      alert('No data to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'Ship Code',
      'Client',
      'Status',
      'Created Date',
      'Package Contents',
      'Weight',
      'Weight Unit',
      'Dimensions (L×W×H)',
      'Size Unit',
      'Value',
      'Currency',
      'Recipient Name',
      'Address',
      'City',
      'Province/State',
      'Postal Code',
      'Country',
      'Tracking Code',
      'Service Type',
      'Items Count',
      'Items Description'
    ];

    // Convert shipment data to CSV rows
    const csvRows = filteredShipments.map(shipment => {
      // Format items description
      const itemsDesc = shipment.items && shipment.items.length > 0
        ? shipment.items.map(item => 
            `${item.quantity || 1}x ${item.description || item.sku || 'Unknown'} (${item.country_of_origin || 'Unknown origin'})`
          ).join('; ')
        : 'No items';

      // Format dimensions
      const dimensions = `${shipment.length || 'N/A'}×${shipment.width || 'N/A'}×${shipment.height || 'N/A'}`;

      // Remove 'OLD__' prefix from client name if present
      const clientName = (shipment.clientName || '').replace(/^OLD__/, '');

      return [
        shipment.ship_code || '',
        clientName,
        shipment.status || '',
        shipment.created_at ? new Date(shipment.created_at).toLocaleString() : '',
        shipment.package_contents || '',
        shipment.weight || '',
        shipment.weight_unit || '',
        dimensions,
        shipment.size_unit || '',
        shipment.value || '0',
        shipment.currency || 'USD',
        shipment.name || '',
        shipment.address1 || '',
        shipment.city || '',
        shipment.province_code || '',
        shipment.postal_code || '',
        shipment.country_code || '',
        shipment.tracking_code || '',
        shipment.postage_type || '',
        shipment.items ? shipment.items.length : 0,
        itemsDesc
      ];
    });

    // Convert to CSV format
    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => 
        row.map(cell => {
          // Escape cells containing commas or quotes
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    // Generate filename with date and filter info
    const dateStr = new Date().toISOString().slice(0, 10);
    const statusStr = selectedStatuses.join('-');
    const clientStr = selectAllClients ? 'AllClients' : selectedClient.replace(/\s+/g, '_');
    const filename = `stallion_export_${clientStr}_${statusStr}_${dateStr}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="stallion-status-container">
      <div className="header-section">
        <h1>Stallion Shipment Status</h1>
        {lastRefreshed && (
          <div className="last-refreshed">
            Last refreshed: {formatDate(lastRefreshed)}
            <button onClick={handleRefresh} className="refresh-button" disabled={isLoading || isLoadingMore}>
              {isLoading || isLoadingMore ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        )}
      </div>
      
      <div className="clients-container">
        <h2>US Clients</h2>
        {isLoading && !shipmentData ? (
          <div className="loading">Loading clients...</div>
        ) : (
          <>
            <div className="client-actions">
              <button 
                onClick={fetchAllClientsData} 
                className={`select-all-clients-btn ${selectAllClients ? 'selected' : ''}`}
                disabled={isLoading || isLoadingMore}
              >
                {isLoading || isLoadingMore ? 'Loading All Clients...' : 'Select All Clients'}
              </button>
            </div>
            <div className="client-list">
              {clients.map((client) => (
                <div 
                  key={client.API_KEY} 
                  className={`client-item ${selectedClient === client.CLIENT ? 'selected' : ''}`}
                  onClick={() => fetchShipmentData(client.API_KEY, client.CLIENT)}
                >
                  {client.CLIENT}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {shipmentData && (
        <div className="shipment-data-container">
          {isLoading ? (
            <div className="loading">Loading shipment data...</div>
          ) : (
            <div className="shipment-results">
              <h2>{selectedClient}</h2>
              
              <div className="date-filters-top">
                <div className="date-filter">
                  <label htmlFor="date-from-top">From Date:</label>
                  <input 
                    type="date" 
                    id="date-from-top"
                    value={dateFilterFrom}
                    onChange={(e) => {
                      setDateFilterFrom(e.target.value);
                      setSelectedShipment(null);
                    }}
                    className="date-input"
                  />
                </div>
                <div className="date-filter">
                  <label htmlFor="date-to-top">To Date:</label>
                  <input 
                    type="date" 
                    id="date-to-top"
                    value={dateFilterTo}
                    onChange={(e) => {
                      setDateFilterTo(e.target.value);
                      setSelectedShipment(null);
                    }}
                    className="date-input"
                  />
                </div>
                {(dateFilterFrom || dateFilterTo) && (
                  <button 
                    onClick={() => {
                      setDateFilterFrom('');
                      setDateFilterTo('');
                      setSelectedShipment(null);
                    }}
                    className="clear-dates-btn"
                  >
                    Clear Dates
                  </button>
                )}
              </div>
              
              <div className="status-actions">
                <button onClick={handleSelectAllStatuses} className="status-action-btn">Select All</button>
                <button onClick={handleClearStatuses} className="status-action-btn">Clear</button>
              </div>
              
              <div className="status-summary">
                {Object.entries(statusCounts).map(([status, count]) => (
                  <div 
                    key={status}
                    className={`status-box ${selectedStatuses.includes(status) ? 'active' : ''}`}
                    onClick={() => handleStatusToggle(status)}
                  >
                    <h3>{status.charAt(0).toUpperCase() + status.slice(1)}</h3>
                    <p className="count">{count}</p>
                  </div>
                ))}
                <div className="status-box total">
                  <h3>Total</h3>
                  <p className="count">{totalCount}</p>
                </div>
              </div>
              
              {selectAllClients && Object.keys(clientBreakdown).length > 0 && (
                <div className="client-breakdown-section">
                  <h3>Client Breakdown - Pending Shipments</h3>
                  <div className="client-breakdown-table-container">
                    <table className="client-breakdown-table">
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th>Ready</th>
                          <th>Received</th>
                          <th>Processing</th>
                          <th>Total Pending</th>
                          <th>Pending Value</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.values(clientBreakdown)
                          .filter(client => client.pendingBoxes > 0)
                          .sort((a, b) => b.pendingValue - a.pendingValue)
                          .map(client => (
                            <React.Fragment key={client.clientName}>
                              <tr className="client-breakdown-row">
                                <td className="client-name-cell">{client.clientName}</td>
                                <td className="status-count-cell">
                                  {client.statuses.ready ? client.statuses.ready.count : 0}
                                </td>
                                <td className="status-count-cell">
                                  {client.statuses.received ? client.statuses.received.count : 0}
                                </td>
                                <td className="status-count-cell">
                                  {client.statuses.processing ? client.statuses.processing.count : 0}
                                </td>
                                <td className="total-pending-cell">{client.pendingBoxes}</td>
                                <td className="pending-value-cell">{formatCurrency(client.pendingValue)}</td>
                                <td className="actions-cell">
                                  <button
                                    onClick={() => toggleClientExpansion(client.clientName)}
                                    className="expand-btn"
                                  >
                                    {expandedClients.has(client.clientName) ? 'Collapse' : 'Expand'}
                                  </button>
                                  <button
                                    onClick={() => generateEmailTemplate(client)}
                                    className="email-btn"
                                    style={{ marginLeft: '5px' }}
                                  >
                                    Email Template
                                  </button>
                                  <button
                                    onClick={() => showGroupingAnalysis(client)}
                                    className="grouping-btn"
                                    style={{ marginLeft: '5px' }}
                                  >
                                    Group Shipments
                                  </button>
                                </td>
                              </tr>
                              {expandedClients.has(client.clientName) && (
                                <tr className="client-details-row">
                                  <td colSpan="7">
                                    <div className="client-shipments-details">
                                      <h4>Pending Shipments Details for {client.clientName}</h4>
                                      <div className="shipments-detail-table-container">
                                        <table className="shipments-detail-table">
                                          <thead>
                                            <tr>
                                              <th>Ship Code</th>
                                              <th>Status</th>
                                              <th>Created</th>
                                              <th>Contents</th>
                                              <th>Items</th>
                                              <th>Value</th>
                                              <th>Address</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {client.shipments.map(shipment => (
                                              <tr key={shipment.ship_code}>
                                                <td>{shipment.ship_code}</td>
                                                <td>{shipment.status}</td>
                                                <td>{formatDate(shipment.created_at)}</td>
                                                <td>{shipment.package_contents || 'N/A'}</td>
                                                <td>
                                                  {shipment.items && shipment.items.length > 0 ? (
                                                    <div className="items-list">
                                                      {shipment.items.map((item, idx) => (
                                                        <div key={idx} className="item-detail">
                                                          {item.quantity}x {item.description || item.sku} - {formatCurrency(item.value)}
                                                        </div>
                                                      ))}
                                                    </div>
                                                  ) : 'No items'}
                                                </td>
                                                <td>{formatCurrency(shipment.value)}</td>
                                                <td>{shipment.full_address || normalizeAddress(
                                                  shipment.address1,
                                                  shipment.city,
                                                  shipment.province_code,
                                                  shipment.postal_code
                                                )}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              <div className="address-summary-toggle">
                <button onClick={handleToggleAddressSummary} className="toggle-btn">
                  {showAddressSummary ? 'Hide Address Summary' : 'Show Address Summary'}
                </button>
              </div>
              
              {showAddressSummary && (
                <div className="address-summary">
                  <h3>Address Distribution Summary</h3>
                  <p className="summary-description">
                    Distribution of shipments for selected statuses across Addresses
                    {selectedCountry && (
                      <span className="country-filter-badge"> (Filtered by {selectedCountry} origin)</span>
                    )}
                    {(dateFilterFrom || dateFilterTo) && (
                      <span className="date-filter-badge">
                        {' '}(Filtered by date: {dateFilterFrom || 'any'} to {dateFilterTo || 'any'})
                      </span>
                    )}
                  </p>
                  
                  {/* If filtering by country, show a note about what's displayed */}
                  {selectedCountry && (
                    <div className="filter-note">
                      <p>
                        <strong>Note:</strong> Only showing boxes containing items from {selectedCountry}. 
                        {addressSummaryData.length === 0 ? (
                          <span className="no-match-warning"> No shipments match this filter.</span>
                        ) : null}
                      </p>
                    </div>
                  )}
                  
                  {addressSummaryData.length > 0 ? (
                    <div className="address-summary-table-container">
                      <table className="address-summary-table">
                        <thead>
                          <tr>
                            <th onClick={() => handleAddressSort('address')}>
                              Address {getAddressSortIndicator('address')}
                            </th>
                            <th onClick={() => handleAddressSort('total')}>
                              Total Boxes {getAddressSortIndicator('total')}
                            </th>
                            <th onClick={() => handleAddressSort('totalValue')}>
                              Total Value {getAddressSortIndicator('totalValue')}
                            </th>
                            <th onClick={() => handleAddressSort('pendingValue')}>
                              Pending Value {getAddressSortIndicator('pendingValue')}
                            </th>
                            <th onClick={() => handleAddressSort('clearanceDate')}>
                              Est. Clearance {getAddressSortIndicator('clearanceDate')}
                            </th>
                            <th onClick={() => handleAddressSort('daysToClean')}>
                              Days to Clear {getAddressSortIndicator('daysToClean')}
                            </th>
                            <th>Status Breakdown</th>
                          </tr>
                        </thead>
                        <tbody>
                          {addressSummaryData.map(addr => (
                            <tr 
                              key={addr.address} 
                              className={`address-summary-row ${selectedAddress === addr.address ? 'selected' : ''}`}
                              onClick={() => setSelectedAddress(selectedAddress === addr.address ? '' : addr.address)}
                            >
                              <td className="address-cell">{addr.address}</td>
                              <td className="address-total-boxes">{addr.total}</td>
                              <td className="address-total-value-cell">{formatCurrency(addr.totalValue)}</td>
                              <td className="address-pending-value-cell">{formatCurrency(addr.pendingValue)}</td>
                              <td className="address-clearance-date-cell">
                                {addr.pendingValue > 0 
                                  ? formatDateShort(addr.clearanceDate)
                                  : 'N/A'}
                              </td>
                              <td className="address-days-cell">
                                {addr.pendingValue > 0 ? addr.daysToClean : 'N/A'}
                              </td>
                              <td className="address-status-breakdown-cell">
                                <div className="address-status-breakdown-table">
                                  {Object.entries(addr.byStatus).map(([status, data]) => (
                                    <div key={status} className="address-status-row">
                                      <span className="status-name">{status.charAt(0).toUpperCase() + status.slice(1)}:</span>
                                      <span className="status-count">{data.count}</span>
                                      <span className="status-value">{formatCurrency(data.value)}</span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-address-data">
                      <p>No addresses have shipments matching the current filters.</p>
                    </div>
                  )}
                </div>
              )}
              
              <div className="filters-section">
                <div className="filters-group">
                  {availableCountries.length > 0 && (
                    <div className="country-filter">
                      <label htmlFor="country-select">Filter by Country of Origin:</label>
                      <select 
                        id="country-select" 
                        value={selectedCountry} 
                        onChange={handleCountryChange}
                        className="country-select"
                      >
                        <option value="">All Countries</option>
                        {availableCountries.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  <div className="address-filter">
                    <label htmlFor="address-select">Filter by Address:</label>
                    <select 
                      id="address-select" 
                      value={selectedAddress} 
                      onChange={handleAddressChange}
                      className="address-select"
                    >
                      <option value="">All Addresses</option>
                      {addresses.map(address => (
                        <option key={address} value={address}>{address}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="view-controls">
                  <div className="view-selector">
                    <button 
                      className={`view-mode-btn ${viewMode === 'card' ? 'active' : ''}`}
                      onClick={() => handleViewModeChange('card')}
                    >
                      Card View
                    </button>
                    <button 
                      className={`view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
                      onClick={() => handleViewModeChange('table')}
                    >
                      Table View
                    </button>
                  </div>
                  
                  <div className="toggle-raw-data">
                    <button onClick={() => setShowRawData(!showRawData)}>
                      {showRawData ? 'Hide Raw Data' : 'Show Raw Data'}
                    </button>
                  </div>
                </div>
              </div>
              
              {showRawData && (
                <div className="raw-data">
                  <h3>Raw API Response</h3>
                  <pre>{JSON.stringify(shipmentData.rawData, null, 2)}</pre>
                </div>
              )}
              
              {isLoadingMore && (
                <div className="loading-more">
                  <p>Loading all shipments...</p>
                </div>
              )}
              
              {!isLoadingMore && filteredShipments.length > 0 ? (
                <div className="shipment-table-container">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div>
                      <h3>
                        {selectedStatuses.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')} Shipments
                        {selectedCountry && ` (Filtered by ${selectedCountry})`}
                        {selectedAddress && ` (Filtered by Address)`}
                        {(dateFilterFrom || dateFilterTo) && ` (Date: ${dateFilterFrom || 'any'} to ${dateFilterTo || 'any'})`}
                      </h3>
                      <p className="shipment-count">{filteredShipments.length} shipments found</p>
                    </div>
                    {selectAllClients && (
                      <button 
                        onClick={exportToCSV}
                        className="export-csv-btn"
                        style={{
                          backgroundColor: '#10b981',
                          color: 'white',
                          padding: '8px 16px',
                          borderRadius: '4px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                      >
                        Export to CSV
                      </button>
                    )}
                  </div>
                  
                  {viewMode === 'card' ? (
                    <div className="boxes-container">
                      {filteredShipments.map((shipment) => (
                        <div key={shipment.ship_code} className="box-card">
                          <div 
                            className={`box-header ${selectedShipment && selectedShipment.ship_code === shipment.ship_code ? 'selected' : ''}`}
                            onClick={() => handleShipmentSelect(shipment)}
                          >
                            <div className="box-title">
                              <div className="box-code">
                                <span className="label">Ship Code:</span>
                                <span className="value">{shipment.ship_code || 'N/A'}</span>
                              </div>
                            </div>
                            <div className="box-date">
                              <span className="label">Created:</span>
                              <span className="value">{formatDate(shipment.created_at)}</span>
                            </div>
                            <div className="toggle-icon">
                              {selectedShipment && selectedShipment.ship_code === shipment.ship_code ? '−' : '+'}
                            </div>
                          </div>
                          
                          <div className="box-summary">
                            <div className="box-detail">
                              <span className="label">Package Contents:</span>
                              <span className="value">{shipment.package_contents || 'N/A'}</span>
                            </div>
                            <div className="box-detail">
                              <span className="label">Dimensions:</span>
                              <span className="value">{`${shipment.length || 'N/A'} × ${shipment.width || 'N/A'} × ${shipment.height || 'N/A'} ${shipment.size_unit || ''}`}</span>
                            </div>
                            <div className="box-detail">
                              <span className="label">Weight:</span>
                              <span className="value">{`${shipment.weight || 'N/A'} ${shipment.weight_unit || ''}`}</span>
                            </div>
                            <div className="box-detail">
                              <span className="label">Value:</span>
                              <span className="value">{formatCurrency(shipment.value, shipment.currency)}</span>
                            </div>
                            <div className="box-detail">
                              <span className="label">Address:</span>
                              <span className="value">{shipment.full_address}</span>
                            </div>
                          </div>
                          
                          {selectedShipment && selectedShipment.ship_code === shipment.ship_code && (
                            <div className="box-details-expanded">
                              <div className="shipment-details-section">
                                <h4>Shipping Details</h4>
                                <div className="details-grid">
                                  <div className="detail-item">
                                    <span className="detail-label">Name:</span>
                                    <span className="detail-value">{shipment.name || 'N/A'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-label">Address:</span>
                                    <span className="detail-value">
                                      {shipment.address1 || 'N/A'}
                                      {shipment.address2 && `, ${shipment.address2}`}
                                    </span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-label">City:</span>
                                    <span className="detail-value">{shipment.city || 'N/A'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-label">State/Province:</span>
                                    <span className="detail-value">{shipment.province_code || 'N/A'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-label">Postal Code:</span>
                                    <span className="detail-value">{shipment.postal_code || 'N/A'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-label">Country:</span>
                                    <span className="detail-value">{shipment.country_code || 'N/A'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-label">Package Type:</span>
                                    <span className="detail-value">{shipment.package_type || 'N/A'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-label">Service:</span>
                                    <span className="detail-value">{shipment.postage_type || 'N/A'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-label">Tracking #:</span>
                                    <span className="detail-value">{shipment.tracking_code || 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="shipment-details-section">
                                <h4>Items in Box ({shipment.items?.length || 0})</h4>
                                {renderShipmentItems(shipment.items)}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="excel-table-container">
                      <table className="excel-table">
                        <thead>
                          <tr>
                            <th onClick={() => handleSort('ship_code')}>
                              Ship Code {getSortIndicator('ship_code')}
                            </th>
                            {selectAllClients && (
                              <th onClick={() => handleSort('clientName')}>
                                Client {getSortIndicator('clientName')}
                              </th>
                            )}
                            <th onClick={() => handleSort('status')}>
                              Status {getSortIndicator('status')}
                            </th>
                            <th onClick={() => handleSort('created_at')}>
                              Created {getSortIndicator('created_at')}
                            </th>
                            <th onClick={() => handleSort('weight')}>
                              Weight {getSortIndicator('weight')}
                            </th>
                            <th onClick={() => handleSort('value')}>
                              Value {getSortIndicator('value')}
                            </th>
                            <th onClick={() => handleSort('name')}>
                              Recipient {getSortIndicator('name')}
                            </th>
                            <th onClick={() => handleSort('address1')}>
                              Address {getSortIndicator('address1')}
                            </th>
                            <th className="items-col">Items Information</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredShipments.map((shipment) => {
                            const itemsWithOrigins = getItemsWithOrigins(shipment.items);
                            
                            return (
                              <tr 
                                key={shipment.ship_code}
                                className={selectedShipment && selectedShipment.ship_code === shipment.ship_code ? 'selected-row' : ''}
                                onClick={() => handleShipmentSelect(shipment)}
                              >
                                <td>{shipment.ship_code || 'N/A'}</td>
                                {selectAllClients && (
                                  <td>{shipment.clientName || 'N/A'}</td>
                                )}
                                <td>{shipment.status || 'N/A'}</td>
                                <td>{formatDate(shipment.created_at)}</td>
                                <td>{`${shipment.weight || 'N/A'} ${shipment.weight_unit || ''}`}</td>
                                <td>{formatCurrency(shipment.value, shipment.currency)}</td>
                                <td>{shipment.name || 'N/A'}</td>
                                <td>
                                  {shipment.full_address}
                                </td>
                                <td className="items-breakdown">
                                  {itemsWithOrigins.length > 0 ? (
                                    <div className="items-table-wrapper">
                                      <table className="embedded-items-table">
                                        <thead>
                                          <tr>
                                            <th>SKU</th>
                                            <th>Description</th>
                                            <th>Qty</th>
                                            <th>Value</th>
                                            <th>Origin</th>
                                            <th>HS Code</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {itemsWithOrigins.map((item, idx) => (
                                            <tr key={idx} className={selectedCountry === item.origin ? 'highlighted-item' : ''}>
                                              <td>{item.sku}</td>
                                              <td>{item.description}</td>
                                              <td>{item.quantity}</td>
                                              <td>{item.value}</td>
                                              <td>{item.origin}</td>
                                              <td>{item.hsCode}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    'No items'
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : !isLoadingMore ? (
                <div className="no-shipments">
                  <p>No shipments found for the selected status(es){selectedCountry ? ` and country (${selectedCountry})` : ''}{selectedAddress ? ` and address` : ''}{(dateFilterFrom || dateFilterTo) ? ` and date range (${dateFilterFrom || 'any'} to ${dateFilterTo || 'any'})` : ''}.</p>
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {!shipmentData && !isLoading && (
        <div className="no-data">
          {error ? (
            <div className="error-message">{error}</div>
          ) : (
            <p>Select a client to view shipment data</p>
          )}
        </div>
      )}

      {showEmailTemplate && (
        <div className="email-template-modal">
          <div className="email-template-content">
            <div className="email-template-header">
              <h2>Email Template for {selectedClientForEmail}</h2>
              <button 
                onClick={() => setShowEmailTemplate(false)}
                className="close-modal-btn"
              >
                ×
              </button>
            </div>
            <div className="email-template-body">
              <textarea 
                value={emailTemplate}
                onChange={(e) => setEmailTemplate(e.target.value)}
                className="email-template-textarea"
                rows="25"
                cols="80"
              />
            </div>
            <div className="email-template-footer">
              <button 
                onClick={copyToClipboard}
                className="copy-btn"
              >
                Copy to Clipboard
              </button>
              <button 
                onClick={() => setShowEmailTemplate(false)}
                className="close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showShipmentGrouping && groupingAnalysis && (
        <div className="shipment-grouping-modal">
          <div className="shipment-grouping-content">
            <div className="shipment-grouping-header">
              <h2>Shipment Grouping Analysis for {selectedClientForGrouping}</h2>
              <button 
                onClick={() => setShowShipmentGrouping(false)}
                className="close-modal-btn"
              >
                ×
              </button>
            </div>
            <div className="shipment-grouping-body">
              <div className="grouping-summary">
                <div className="summary-card">
                  <h3>Summary</h3>
                  <p><strong>Total Boxes in 5+ Shipment:</strong> {groupingAnalysis.totalFivePlusBoxes}</p>
                  <p><strong>Total Boxes in Leftover Shipment:</strong> {groupingAnalysis.totalLeftoverBoxes}</p>
                </div>
              </div>

              <div className="grouping-section">
                <h3>5+ Identical Boxes Shipment</h3>
                {Object.keys(groupingAnalysis.fivePlus).length > 0 ? (
                  <div className="box-groups">
                    {Object.values(groupingAnalysis.fivePlus).map((group, index) => (
                      <div key={index} className="box-group">
                        <div className="group-header">
                          <span className="group-count">{group.count} identical boxes</span>
                        </div>
                        <div className="group-items">
                          <h4>Items in each box:</h4>
                          {group.items && group.items.length > 0 ? (
                            <ul className="items-list-grouped">
                              {group.items.map((item, idx) => (
                                <li key={idx}>
                                  {item.quantity}x {item.description || item.sku || 'Unknown Item'}
                                  {item.value && ` - ${formatCurrency(item.value)}`}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p>No items data</p>
                          )}
                        </div>
                        <div className="group-ship-codes">
                          <details>
                            <summary>Ship Codes ({group.count})</summary>
                            <div className="ship-codes-list">
                              {group.shipments.map((shipment, idx) => (
                                <span key={idx} className="ship-code-tag">
                                  {shipment.ship_code}
                                </span>
                              ))}
                            </div>
                          </details>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-groups">No box groups with 5 or more identical boxes</p>
                )}
              </div>

              <div className="grouping-section">
                <h3>Leftover Shipment (Less than 5 identical boxes)</h3>
                {Object.keys(groupingAnalysis.leftover).length > 0 ? (
                  <div className="box-groups">
                    {Object.values(groupingAnalysis.leftover).map((group, index) => (
                      <div key={index} className="box-group leftover">
                        <div className="group-header">
                          <span className="group-count">{group.count} identical box{group.count > 1 ? 'es' : ''}</span>
                        </div>
                        <div className="group-items">
                          <h4>Items in each box:</h4>
                          {group.items && group.items.length > 0 ? (
                            <ul className="items-list-grouped">
                              {group.items.map((item, idx) => (
                                <li key={idx}>
                                  {item.quantity}x {item.description || item.sku || 'Unknown Item'}
                                  {item.value && ` - ${formatCurrency(item.value)}`}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p>No items data</p>
                          )}
                        </div>
                        <div className="group-ship-codes">
                          <details>
                            <summary>Ship Codes ({group.count})</summary>
                            <div className="ship-codes-list">
                              {group.shipments.map((shipment, idx) => (
                                <span key={idx} className="ship-code-tag">
                                  {shipment.ship_code}
                                </span>
                              ))}
                            </div>
                          </details>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-groups">No leftover boxes</p>
                )}
              </div>
            </div>
            <div className="shipment-grouping-footer">
              <button 
                onClick={() => setShowShipmentGrouping(false)}
                className="close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StallionStatus;